import * as React from 'react'

/**
 * Carga assíncrona com recarga — o "react-query" do Supply, do tamanho do que
 * ele precisa: poucos dados por tela, e toda escrita avisa as telas abertas
 * (`invalidate()`) para recarregarem.
 *
 * - `deps` mudou → recarrega;
 * - alguém chamou `invalidate()` → recarrega em segundo plano (sem piscar o
 *   carregando: `data` antigo continua na tela até o novo chegar);
 * - a aba voltou a ter foco → recarrega (quem deixou a tela da reunião aberta
 *   vê o que os outros mudaram).
 *
 * Com `keepPrevious`, mudar `deps` não limpa a tela: o dado da chave anterior
 * fica visível (`stale: true`) até o novo chegar. Quem usa precisa saber a que
 * chave o dado pertence — devolva a chave junto no próprio dado.
 *
 * Tempo real (`lib/realtime.tsx`): mudança de outra pessoa chama `refreshAll()`.
 * A recarga reaproveita os objetos que não mudaram (`replaceEqualDeep`): a
 * tarefa aberta só vira um objeto novo se ELA mudou — formulário que se
 * sincroniza com `useEffect(..., [task])` não perde o rascunho porque outra
 * coisa da semana mudou.
 */

type Listener = () => void
const listeners = new Set<Listener>()
const localWriteListeners = new Set<Listener>()
let lastLocalWrite = 0

/** Chame depois de qualquer escrita bem-sucedida. */
export function invalidate(): void {
  lastLocalWrite = Date.now()
  for (const l of localWriteListeners) l()
  refreshAll()
}

/** Recarrega as telas abertas sem contar como escrita minha (tempo real). */
export function refreshAll(): void {
  for (const l of listeners) l()
}

/** Momento (ms) da última escrita própria — o tempo real separa "fui eu" de "foi outro". */
export function lastLocalWriteAt(): number {
  return lastLocalWrite
}

/** Avisa a cada escrita própria (`invalidate()`). */
export function onLocalWrite(listener: Listener): () => void {
  localWriteListeners.add(listener)
  return () => {
    localWriteListeners.delete(listener)
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false
  const proto = Object.getPrototypeOf(value) as unknown
  return proto === Object.prototype || proto === null
}

/**
 * Devolve `next`, mas reaproveitando de `prev` todo pedaço igual (mesma ideia
 * do react-query): se nada mudou, devolve o próprio `prev`.
 */
export function replaceEqualDeep<T>(prev: unknown, next: T): T {
  if (prev === next) return next
  const bothArrays = Array.isArray(prev) && Array.isArray(next)
  if (!bothArrays && !(isPlainObject(prev) && isPlainObject(next))) return next
  const p = prev as Record<string, unknown>
  const n = next as Record<string, unknown>
  const keys = Object.keys(n)
  const out = (bothArrays ? [] : {}) as Record<string, unknown>
  let same = Object.keys(p).length === keys.length
  for (const k of keys) {
    const v = replaceEqualDeep(p[k], n[k])
    out[k] = v
    if (v !== p[k] || !(k in p)) same = false
  }
  return (same ? prev : out) as T
}

export interface LoadState<T> {
  readonly data: T | undefined
  readonly error: string | null
  readonly loading: boolean
  /** `data` é de uma chave (`deps`) anterior; a da atual ainda está carregando. */
  readonly stale: boolean
  readonly reload: () => void
}

export function useLoad<T>(
  fn: () => Promise<T>,
  deps: React.DependencyList,
  options?: { pollMs?: number; keepPrevious?: boolean },
): LoadState<T> {
  const [state, setState] = React.useState<{ data: T | undefined; dataKey: string | null; error: string | null; loading: boolean }>({
    data: undefined,
    dataKey: null,
    error: null,
    loading: true,
  })
  const [tick, setTick] = React.useState(0)
  const fnRef = React.useRef(fn)
  fnRef.current = fn

  const reload = React.useCallback(() => setTick((n) => n + 1), [])

  React.useEffect(() => {
    listeners.add(reload)
    const onFocus = () => {
      if (document.visibilityState === 'visible') reload()
    }
    document.addEventListener('visibilitychange', onFocus)
    const poll = options?.pollMs ? window.setInterval(reload, options.pollMs) : undefined
    return () => {
      listeners.delete(reload)
      document.removeEventListener('visibilitychange', onFocus)
      if (poll) window.clearInterval(poll)
    }
  }, [reload, options?.pollMs])

  // Mudou a chave (semana, área…): limpa para não mostrar o dado de outra chave
  // (a não ser com `keepPrevious`, que mantém e marca `stale`).
  const key = JSON.stringify(deps)
  const lastKey = React.useRef(key)
  const keepPrevious = options?.keepPrevious ?? false

  React.useEffect(() => {
    let cancelled = false
    const keyChanged = lastKey.current !== key
    lastKey.current = key
    setState((s) => (keyChanged && !keepPrevious ? { data: undefined, dataKey: null, error: null, loading: true } : { ...s, error: null, loading: true }))
    fnRef
      .current()
      .then((data) => {
        if (cancelled) return
        // Mesma chave: o que não mudou continua sendo o mesmo objeto.
        setState((s) => ({ data: s.dataKey === key ? replaceEqualDeep(s.data, data) : data, dataKey: key, error: null, loading: false }))
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState((s) => ({ ...s, error: e instanceof Error ? e.message : 'Falha ao carregar', loading: false }))
        }
      })
    return () => {
      cancelled = true
    }
  }, [key, tick, keepPrevious])

  const { dataKey, ...rest } = state
  return { ...rest, stale: rest.data !== undefined && dataKey !== key, reload }
}
